import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';
import { ChevronRight } from '@mui/icons-material';
import { format, isValid, parse } from 'date-fns';
import { ClientsFamily, NewClientEntry, NonReturningClientEntry, StaffPeriods } from '../types';
import { periodLabel, signed } from '../utils/metrics';
import { ChartLegend } from './ChartLegend';
import { EmptyState } from './EmptyState';

export const NEW_COLOR = '#2563EB';
export const LOST_COLOR = '#EA580C';
const LIST_PREVIEW = 5;

export interface ClientMovementCardProps {
  clients: ClientsFamily | null;
  periods: StaffPeriods;
}

/** "12 week programme | Coach | Flexi" → "12 week · Coach · Flexi" */
export function shortPricingOption(option: string | null): string {
  if (!option) return 'Unknown programme';
  return option
    .split('|')
    .map((part) => part.replace(/\bprogramme\b/gi, '').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' · ');
}

const formatDay = (value: string | null): string | null => {
  if (!value) return null;
  const date = parse(value, 'yyyy-MM-dd', new Date());
  return isValid(date) ? format(date, 'd MMM yyyy') : value;
};

const formatMonth = (value: string | null): string | null => {
  if (!value) return null;
  const date = parse(value, 'yyyy-MM', new Date());
  return isValid(date) ? format(date, 'MMM yyyy') : value;
};

const newClientDetail = (entry: NewClientEntry): string =>
  [formatDay(entry.activationDate) ? `Activated ${formatDay(entry.activationDate)}` : null, entry.location]
    .filter(Boolean)
    .join(' · ');

const lostClientDetail = (entry: NonReturningClientEntry): string =>
  [
    formatMonth(entry.expirationMonth) ? `Expired ${formatMonth(entry.expirationMonth)}` : null,
    entry.packageCount !== null ? `${entry.packageCount} ${entry.packageCount === 1 ? 'package' : 'packages'}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

interface ClientListProps {
  title: string;
  entries: Array<{ clientId: string; clientName: string | null; detail: string; programme: string }>;
  total: number;
}

const ClientList: React.FC<ClientListProps> = ({ title, entries, total }) => {
  const [showAll, setShowAll] = useState(false);
  const visible = showAll ? entries : entries.slice(0, LIST_PREVIEW);

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="subtitle2" sx={{ px: 0.5, pb: 0.5 }}>
        {title}{' '}
        <Typography component="span" variant="body2" color="text.secondary">
          · {total} in the latest 6 months
        </Typography>
      </Typography>
      <List aria-label={title} dense disablePadding>
        {visible.map((entry) => (
          <ListItem key={entry.clientId} disablePadding divider>
            <ListItemButton component={RouterLink} to={`/clients/${entry.clientId}`} sx={{ gap: 1.5, px: 1 }}>
              <ListItemText
                primary={entry.clientName ?? 'Unknown client'}
                secondary={entry.detail}
                primaryTypographyProps={{ fontWeight: 600, fontSize: '0.875rem' }}
                secondaryTypographyProps={{ fontSize: '0.75rem' }}
              />
              <Chip size="small" label={entry.programme} sx={{ flex: 'none', maxWidth: 220 }} />
              <ChevronRight fontSize="small" sx={{ color: 'text.disabled', flex: 'none' }} />
            </ListItemButton>
          </ListItem>
        ))}
        {visible.length === 0 ? (
          <ListItem>
            <ListItemText secondary="None in the latest 6 months" />
          </ListItem>
        ) : null}
      </List>
      {entries.length > LIST_PREVIEW ? (
        <Button size="small" variant="outlined" sx={{ mt: 1.5 }} onClick={() => setShowAll((current) => !current)}>
          {showAll ? 'Show fewer' : `Show all ${entries.length}`}
        </Button>
      ) : null}
    </Box>
  );
};

interface DivergingRowProps {
  label: string;
  gained: number;
  lost: number;
  net: number;
  scaleMax: number;
  muted?: boolean;
}

const DivergingRow: React.FC<DivergingRowProps> = ({ label, gained, lost, net, scaleMax, muted }) => {
  const width = (value: number) => `${Math.round((value / scaleMax) * 100)}%`;
  const opacity = muted ? 0.55 : 1;
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
      <Typography variant="body2" sx={{ width: 180, flex: 'none', fontWeight: muted ? 400 : 600, color: muted ? 'text.secondary' : 'text.primary' }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', width: 420, maxWidth: '100%', height: 24 }}>
        <Box sx={{ width: '50%', display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 0.75 }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {lost}
          </Typography>
          <Box sx={{ height: 14, width: width(lost), borderRadius: '4px 0 0 4px', bgcolor: LOST_COLOR, opacity }} />
        </Box>
        <Box sx={{ width: '50%', display: 'flex', alignItems: 'center', gap: 0.75, borderLeft: '2px solid #374151' }}>
          <Box sx={{ height: 14, width: width(gained), borderRadius: '0 4px 4px 0', bgcolor: NEW_COLOR, opacity }} />
          <Typography variant="caption" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
            {gained}
          </Typography>
        </Box>
      </Box>
      <Typography variant="body2" color="text.secondary">
        net{' '}
        <Typography component="span" variant="body2" sx={{ fontWeight: 600, color: 'text.primary', fontVariantNumeric: 'tabular-nums' }}>
          {signed(net)}
        </Typography>
      </Typography>
    </Stack>
  );
};

/** New clients versus non-returning clients: a diverging summary per period, then the two lists. */
export const ClientMovementCard: React.FC<ClientMovementCardProps> = ({ clients, periods }) => {
  const scaleMax = clients
    ? Math.max(15, clients.latest.new, clients.latest.nonReturning, clients.prior.new, clients.prior.nonReturning)
    : 15;

  return (
    <Card>
      <CardHeader
        title="Client movement"
        subheader="New clients whose first PT package was mostly with you, against clients whose package expired without ever returning"
        sx={{ pb: 1 }}
      />
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <ChartLegend
          keys={[
            { label: 'New', color: NEW_COLOR },
            { label: 'Non-returning', color: LOST_COLOR },
          ]}
        />
      </Box>
      <CardContent sx={{ pt: 0 }}>
        {clients ? (
          <>
            <Stack spacing={1.25} sx={{ mb: 3 }}>
              <DivergingRow
                label={`Latest · ${periodLabel(periods.latest)}`}
                gained={clients.latest.new}
                lost={clients.latest.nonReturning}
                net={clients.latest.net}
                scaleMax={scaleMax}
              />
              <DivergingRow
                label={`Prior · ${periodLabel(periods.prior)}`}
                gained={clients.prior.new}
                lost={clients.prior.nonReturning}
                net={clients.prior.net}
                scaleMax={scaleMax}
                muted
              />
            </Stack>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <ClientList
                  title="New clients"
                  total={clients.latest.new}
                  entries={clients.newClients
                    .filter((entry) => entry.period === 'latest_6m')
                    .map((entry) => ({
                      clientId: entry.clientId,
                      clientName: entry.clientName,
                      detail: newClientDetail(entry),
                      programme: shortPricingOption(entry.pricingOption),
                    }))}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <ClientList
                  title="Non-returning clients"
                  total={clients.latest.nonReturning}
                  entries={clients.nonReturningClients
                    .filter((entry) => entry.period === 'latest_6m')
                    .map((entry) => ({
                      clientId: entry.clientId,
                      clientName: entry.clientName,
                      detail: lostClientDetail(entry),
                      programme: shortPricingOption(entry.pricingOption),
                    }))}
                />
              </Grid>
            </Grid>
          </>
        ) : (
          <EmptyState title="No client movement recorded" body="No client-change data for this period." />
        )}
      </CardContent>
    </Card>
  );
};
